// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file BreadcrumbItem.tsx
 * @input Uses React use/useRef/useEffect, stylex, theme tokens, BreadcrumbContext
 * @output Exports BreadcrumbItem component and BreadcrumbItemProps
 * @position Individual breadcrumb item; used inside Breadcrumbs
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Breadcrumbs/Breadcrumbs.doc.mjs
 * - /packages/core/src/Breadcrumbs/Breadcrumbs.test.tsx
 * - /packages/core/src/Breadcrumbs/index.ts
 * - /apps/storybook/stories/Breadcrumbs.stories.tsx
 * - /packages/cli/templates/blocks/components/Breadcrumbs/ (showcase blocks)
 */
import React, { use, useRef, useEffect } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { BreadcrumbContext } from "./Breadcrumbs.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Props
// =============================================================================
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const itemStyles = {
  root: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    kogj98: "x1ghz6dp",
    "--separator-display": "xkce8z9 x1ibt0lz",
    $$css: true
  },
  defaultSize: {
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  },
  supportingSize: {
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * An individual breadcrumb item. Renders as a link (`<a>`) or a span
 * depending on whether it represents the current page.
 *
 * Each item renders its own leading separator, hidden on :first-child via
 * CSS. Auto-current detection uses a post-render effect that checks the
 * DOM — no React child introspection.
 *
 * @example
 * ```
 * <BreadcrumbItem href="/projects">Projects</BreadcrumbItem>
 * <BreadcrumbItem isCurrent>My Project</BreadcrumbItem>
 * ```
 */
export function BreadcrumbItem({
  ref,
  as,
  children,
  href,
  onClick,
  isCurrent: isCurrentProp,
  startIcon,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ...rest
}) {
  const ctx = use(BreadcrumbContext);
  const LinkComponent = useLinkComponent(as);
  const isSupporting = ctx.variant === 'supporting';
  const liRef = useRef(null);
  // Points at the element we render to hold the item's content (the
  // link/button/span in the auto-candidate path). Auto-current detection sets
  // aria-current on this instead of guessing the <li>'s last child.
  const contentRef = useRef(null);
  const isCurrent = isCurrentProp === true;
  const isAutoCandidate = isCurrentProp == null;

  // Auto-detect: if no sibling has aria-current="page" and this is the last
  // non-separator item, set aria-current on our content element.
  // Runs as useEffect (not layout) — only sets an aria attribute, no visual change.
  // Placed on the item's content element (the link/button/span after the
  // separator), matching where the explicit `isCurrent` path sets it, so
  // aria-current lands on the actual interactive element — including when the
  // last item is a link — rather than on the outer <li> (navigation-11).
  useEffect(() => {
    if (!isAutoCandidate) {
      return;
    }
    const li = liRef.current;
    if (!li) {
      return;
    }
    const ol = li.parentElement;
    if (!ol) {
      return;
    }

    // All breadcrumb items (li without aria-hidden are items, with aria-hidden are separators — but we no longer have separator lis)
    const items = Array.from(ol.children);
    const isLast = items.length > 0 && items[items.length - 1] === li;
    const hasExplicit = ol.querySelector('[aria-current="page"]');
    if (isLast && !hasExplicit) {
      // We control the element that holds the content (see the auto-candidate
      // render path below), so set aria-current on that ref rather than
      // assuming a positional last child. Fall back to the <li> only if the
      // ref is somehow unresolved.
      const target = contentRef.current ?? li;
      target.setAttribute('aria-current', 'page');
      return () => {
        target.removeAttribute('aria-current');
      };
    }
  });
  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [startIcon && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x78zum5 x6s0dn4 x2lah0s"
      },
      children: startIcon
    }), children]
  });
  if (isCurrent) {
    return /*#__PURE__*/_jsxs("li", {
      ref: mergeRefs(ref, liRef),
      ...mergeProps(themeProps('breadcrumb-item'), stylex.props(itemStyles.root, isSupporting ? itemStyles.supportingSize : itemStyles.defaultSize, xstyle), className, style),
      "data-testid": testId,
      ...rest,
      children: [/*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        ...{
          className: "x11ke7fs x6s0dn4 xv1l7n4 xu0wf1k x87ps6o"
        },
        children: ctx.separator
      }), /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz x1tgivj0"
          },
          1: {
            className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz xv1l7n4"
          }
        }[!!isSupporting << 0],
        "aria-current": "page",
        children: content
      })]
    });
  }

  // Items without isCurrent set render as links (if href) or plain spans.
  // The effect handles adding aria-current for auto-last detection.
  return /*#__PURE__*/_jsxs("li", {
    ref: mergeRefs(ref, liRef),
    ...mergeProps(themeProps('breadcrumb-item'), stylex.props(itemStyles.root, isSupporting ? itemStyles.supportingSize : itemStyles.defaultSize, xstyle), className, style),
    "data-testid": testId,
    ...rest,
    children: [/*#__PURE__*/_jsx("span", {
      "aria-hidden": "true",
      ...{
        className: "x11ke7fs x6s0dn4 xv1l7n4 xu0wf1k x87ps6o"
      },
      children: ctx.separator
    }), href != null ? /*#__PURE__*/_jsx(LinkComponent, {
      ref: contentRef,
      href: href,
      onClick: onClick,
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk xv1l7n4"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk xv1l7n4"
        }
      }[!!isSupporting << 0],
      children: content
    }) : onClick != null ? /*#__PURE__*/_jsx("button", {
      ref: contentRef,
      type: "button",
      onClick: onClick,
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk x1717udv x1ghz6dp xln7xf2 xv1l7n4"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk x1717udv x1ghz6dp xln7xf2 xv1l7n4"
        }
      }[!!isSupporting << 0],
      children: content
    }) : /*#__PURE__*/_jsx("span", {
      ref: contentRef,
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz x1tgivj0"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz xv1l7n4"
        }
      }[!!isSupporting << 0],
      children: content
    })]
  });
}
BreadcrumbItem.displayName = 'BreadcrumbItem';